import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  Plus,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  Video,
  Image as ImageIcon,
  Star,
  Quote,
  ExternalLink,
  Play,
  Upload,
  X,
  Check,
  Calendar,
  Users,
  TrendingUp,
  Award,
  Loader2,
  Globe,
  FileText
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/axios';

const CATEGORIES = [
  { id: 'logo', name: 'Logo & Identité', color: 'bg-cyan-500' },
  { id: 'print', name: 'Print & Visuels', color: 'bg-orange-500' },
  { id: 'video', name: 'Vidéo Corporate', color: 'bg-purple-500' },
  { id: 'website', name: 'Site Web', color: 'bg-emerald-500' },
  { id: 'app', name: 'Application Mobile', color: 'bg-blue-500' },
  { id: 'marketing', name: 'Marketing Digital', color: 'bg-amber-500' }
];

const CaseStudiesPage = () => {
  const { user } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [caseStudies, setCaseStudies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingCase, setEditingCase] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    client_name: '',
    category: 'logo',
    description: '',
    challenge: '',
    solution: '',
    results: '',
    testimonial_text: '',
    testimonial_author: '',
    testimonial_role: '',
    testimonial_video_url: '',
    cover_image: '',
    gallery: [],
    tags: [],
    stats: [],
    featured: false,
    published: false
  });

  useEffect(() => {
    fetchCaseStudies();
  }, []);

  const fetchCaseStudies = async () => {
    try {
      const res = await api.get('/website/case-studies');
      setCaseStudies(res.data);
    } catch (error) {
      // Mock data
      setCaseStudies([
        {
          id: 'case-1',
          title: 'Refonte identité visuelle TechCorp',
          client_name: 'TechCorp Solutions',
          category: 'logo',
          description: 'Création complète de l\'identité visuelle pour une startup tech en pleine croissance.',
          challenge: 'TechCorp avait besoin d\'une identité moderne qui reflète leur expertise en IA.',
          solution: 'Nous avons créé un logo dynamique avec une palette de couleurs tech et une charte complète.',
          results: 'Augmentation de 45% de la notoriété de marque en 6 mois.',
          testimonial_text: 'L\'équipe MSC a parfaitement capté notre vision. Le résultat dépasse nos attentes !',
          testimonial_author: 'Marie Dupont',
          testimonial_role: 'CEO, TechCorp',
          testimonial_video_url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
          cover_image: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=800',
          gallery: [
            'https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=400',
            'https://images.unsplash.com/photo-1557426272-fc759fdf7a8d?w=400'
          ],
          tags: ['branding', 'logo', 'charte graphique'],
          stats: [
            { label: 'Notoriété', value: '+45%' },
            { label: 'Leads', value: '+120%' }
          ],
          featured: true,
          published: true,
          created_at: '2024-06-15'
        },
        {
          id: 'case-2',
          title: 'Site e-commerce GreenLife',
          client_name: 'GreenLife Bio',
          category: 'website',
          description: 'Développement d\'une plateforme e-commerce eco-responsable.',
          challenge: 'Créer une expérience d\'achat fluide tout en mettant en avant les valeurs écologiques.',
          solution: 'Site performant avec calculateur d\'impact environnemental intégré.',
          results: 'Taux de conversion de 4.2%, supérieur à la moyenne du secteur.',
          testimonial_text: 'Notre chiffre d\'affaires a doublé depuis le lancement du nouveau site.',
          testimonial_author: 'Sophie Martin',
          testimonial_role: 'Fondatrice, GreenLife',
          cover_image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800',
          gallery: [],
          tags: ['e-commerce', 'web', 'shopify'],
          stats: [
            { label: 'Conversion', value: '4.2%' },
            { label: 'CA', value: 'x2' }
          ],
          featured: true,
          published: true,
          created_at: '2024-08-20'
        },
        {
          id: 'case-3',
          title: 'Film corporate RetailMax',
          client_name: 'RetailMax',
          category: 'video',
          description: 'Réalisation d\'un film institutionnel pour les 20 ans de l\'entreprise.',
          challenge: 'Raconter 20 ans d\'histoire en 3 minutes captivantes.',
          solution: 'Interviews croisées, motion design et storytelling émotionnel.',
          results: '+500k vues sur LinkedIn, repris par la presse spécialisée.',
          testimonial_text: 'Une équipe créative et professionnelle. Le film a eu un impact énorme en interne.',
          testimonial_author: 'Pierre Leblanc',
          testimonial_role: 'DirCom, RetailMax',
          testimonial_video_url: '',
          cover_image: 'https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=800',
          gallery: [],
          tags: ['vidéo', 'corporate', 'motion design'],
          stats: [
            { label: 'Vues', value: '500k+' },
            { label: 'Partages', value: '2.5k' }
          ],
          featured: false,
          published: true,
          created_at: '2024-09-10'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingCase) {
        await api.put(`/website/case-studies/${editingCase.id}`, formData);
        toast.success('Étude de cas mise à jour !');
      } else {
        await api.post('/website/case-studies', formData);
        toast.success('Étude de cas créée !');
      }
      fetchCaseStudies();
    } catch (error) {
      // Mock save
      if (editingCase) {
        setCaseStudies(prev => prev.map(c => c.id === editingCase.id ? { ...c, ...formData } : c));
      } else {
        setCaseStudies(prev => [...prev, { ...formData, id: `case-${Date.now()}`, created_at: new Date().toISOString() }]);
      }
      toast.success(editingCase ? 'Mise à jour !' : 'Créée !');
    }
    setShowModal(false);
    resetForm();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette étude de cas ?')) return;
    try {
      await api.delete(`/website/case-studies/${id}`);
      toast.success('Supprimée !');
    } catch (error) {
      // Mock
    }
    setCaseStudies(prev => prev.filter(c => c.id !== id));
  };

  const resetForm = () => {
    setFormData({
      title: '', client_name: '', category: 'logo', description: '', challenge: '', solution: '',
      results: '', testimonial_text: '', testimonial_author: '', testimonial_role: '',
      testimonial_video_url: '', cover_image: '', gallery: [], tags: [], stats: [],
      featured: false, published: false
    });
    setEditingCase(null);
  };

  const openEdit = (caseStudy) => {
    setEditingCase(caseStudy);
    setFormData(caseStudy);
    setShowModal(true);
  };

  const filteredCases = caseStudies.filter(c => {
    const matchesSearch = c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          c.client_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || c.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getCategoryInfo = (catId) => CATEGORIES.find(c => c.id === catId) || CATEGORIES[0];

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn("transition-all duration-300 p-6 md:p-8 pt-24", collapsed ? "md:ml-20" : "md:ml-64")}>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#1a365d]">Études de cas</h1>
            <p className="text-[#64748b]">Gérez vos réalisations et témoignages clients</p>
          </div>
          <Button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle étude de cas
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total', value: caseStudies.length, icon: FileText, color: 'text-blue-600' },
            { label: 'Publiées', value: caseStudies.filter(c => c.published).length, icon: Globe, color: 'text-green-600' },
            { label: 'En vedette', value: caseStudies.filter(c => c.featured).length, icon: Star, color: 'text-amber-600' },
            { label: 'Avec vidéo', value: caseStudies.filter(c => c.testimonial_video_url).length, icon: Video, color: 'text-purple-600' }
          ].map((stat, i) => (
            <Card key={i} className="card">
              <CardContent className="p-4 flex items-center gap-3">
                <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center bg-slate-100", stat.color)}>
                  <stat.icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1a365d]">{stat.value}</p>
                  <p className="text-xs text-[#64748b]">{stat.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
            <Input
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory('all')}
            >
              Tous
            </Button>
            {CATEGORIES.map(cat => (
              <Button
                key={cat.id}
                variant={selectedCategory === cat.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(cat.id)}
              >
                {cat.name}
              </Button>
            ))}
          </div>
        </div>

        {/* Case Studies Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCases.map(caseStudy => {
              const category = getCategoryInfo(caseStudy.category);
              return (
                <Card key={caseStudy.id} className="card overflow-hidden group">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={caseStudy.cover_image || 'https://images.unsplash.com/photo-1557426272-fc759fdf7a8d?w=800'}
                      alt={caseStudy.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute top-3 left-3 flex gap-2">
                      <Badge className={cn(category.color, "text-white")}>{category.name}</Badge>
                      {caseStudy.featured && <Badge className="bg-amber-500 text-white"><Star className="w-3 h-3 mr-1" />Vedette</Badge>}
                    </div>
                    <div className="absolute top-3 right-3">
                      <Badge variant={caseStudy.published ? 'default' : 'secondary'}>
                        {caseStudy.published ? 'Publié' : 'Brouillon'}
                      </Badge>
                    </div>
                    {caseStudy.testimonial_video_url && (
                      <div className="absolute bottom-3 right-3">
                        <div className="w-10 h-10 bg-white/90 rounded-full flex items-center justify-center">
                          <Play className="w-5 h-5 text-purple-600 ml-0.5" />
                        </div>
                      </div>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-[#1a365d] mb-1 line-clamp-1">{caseStudy.title}</h3>
                    <p className="text-sm text-[#64748b] mb-3">{caseStudy.client_name}</p>
                    <p className="text-sm text-[#64748b] mb-4 line-clamp-2">{caseStudy.description}</p>
                    
                    {caseStudy.stats?.length > 0 && (
                      <div className="flex gap-4 mb-4">
                        {caseStudy.stats.slice(0, 2).map((stat, i) => (
                          <div key={i} className="text-center">
                            <p className="text-lg font-bold text-cyan-600">{stat.value}</p>
                            <p className="text-xs text-[#64748b]">{stat.label}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="flex-1" onClick={() => openEdit(caseStudy)}>
                        <Edit className="w-3 h-3 mr-1" />
                        Modifier
                      </Button>
                      <Button variant="outline" size="sm" asChild>
                        <a href={`/site/realisations/${caseStudy.id}`} target="_blank" rel="noreferrer">
                          <Eye className="w-3 h-3" />
                        </a>
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(caseStudy.id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCase ? 'Modifier l\'étude de cas' : 'Nouvelle étude de cas'}</DialogTitle>
          </DialogHeader>
          
          <div className="grid md:grid-cols-2 gap-6 py-4">
            {/* Left Column */}
            <div className="space-y-4">
              <div>
                <Label>Titre du projet *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Ex: Refonte identité visuelle TechCorp"
                />
              </div>
              <div>
                <Label>Nom du client *</Label>
                <Input
                  value={formData.client_name}
                  onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                  placeholder="Ex: TechCorp Solutions"
                />
              </div>
              <div>
                <Label>Catégorie</Label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg p-2"
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Description courte</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Résumé du projet en 2-3 phrases"
                />
              </div>
              <div>
                <Label>Le défi</Label>
                <Textarea
                  value={formData.challenge}
                  onChange={(e) => setFormData({...formData, challenge: e.target.value})}
                  placeholder="Quel était le problème à résoudre ?"
                />
              </div>
              <div>
                <Label>Notre solution</Label>
                <Textarea
                  value={formData.solution}
                  onChange={(e) => setFormData({...formData, solution: e.target.value})}
                  placeholder="Comment avez-vous répondu au défi ?"
                />
              </div>
              <div>
                <Label>Résultats</Label>
                <Textarea
                  value={formData.results}
                  onChange={(e) => setFormData({...formData, results: e.target.value})}
                  placeholder="Chiffres clés, impact mesurable"
                />
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              <div>
                <Label>Image de couverture (URL)</Label>
                <Input
                  value={formData.cover_image}
                  onChange={(e) => setFormData({...formData, cover_image: e.target.value})}
                  placeholder="https://..."
                />
                {formData.cover_image && (
                  <img src={formData.cover_image} alt="Preview" className="mt-2 h-32 w-full object-cover rounded-lg" />
                )}
              </div>

              <div className="border-t pt-4">
                <Label className="flex items-center gap-2 mb-3">
                  <Quote className="w-4 h-4 text-purple-500" />
                  Témoignage client
                </Label>
                <div className="space-y-3">
                  <Textarea
                    value={formData.testimonial_text}
                    onChange={(e) => setFormData({...formData, testimonial_text: e.target.value})}
                    placeholder="Citation du client..."
                    className="min-h-[80px]"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      value={formData.testimonial_author}
                      onChange={(e) => setFormData({...formData, testimonial_author: e.target.value})}
                      placeholder="Nom de l'auteur"
                    />
                    <Input
                      value={formData.testimonial_role}
                      onChange={(e) => setFormData({...formData, testimonial_role: e.target.value})}
                      placeholder="Poste, Entreprise"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-purple-600 flex items-center gap-1">
                      <Video className="w-3 h-3" /> URL vidéo témoignage (optionnel)
                    </Label>
                    <Input
                      value={formData.testimonial_video_url}
                      onChange={(e) => setFormData({...formData, testimonial_video_url: e.target.value})}
                      placeholder="https://youtube.com/watch?v=..."
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4 flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.featured}
                    onChange={(e) => setFormData({...formData, featured: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <Star className="w-4 h-4 text-amber-500" />
                  <span className="text-sm">En vedette</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.published}
                    onChange={(e) => setFormData({...formData, published: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <Globe className="w-4 h-4 text-green-500" />
                  <span className="text-sm">Publier</span>
                </label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Annuler</Button>
            <Button onClick={handleSubmit} className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white">
              {editingCase ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CaseStudiesPage;
